/**
 * The ONE YAML loader for imports (single files — OpenAPI, Postman/Insomnia YAML, Bruno
 * YAML, OpenCollection — and every YAML file of a Bruno folder), so the Import dialog, the
 * sidebar drop, the in-app AI and MCP `spec_import` (including `dry_run`) share the guard.
 *
 * Why: js-yaml 4 keeps an alias (`*a`) as a SHARED REFERENCE to the anchored node, so a 2 KB
 * "billion laughs" file is a small DAG — and every importer walks it as a TREE, expanding it
 * into millions of requests or a gigabyte string on the main thread (tester F1, 27.09.2026).
 * js-yaml has no alias limit of its own.
 *
 * Mechanism — an expanded-size budget, measured BEFORE any importer walks the value:
 * the size of each node as a tree (1 + its children, plus string lengths) is computed once
 * per distinct object and memoised, so the measurement is linear in the DOCUMENT even when
 * the expansion is exponential. A cycle, or an expansion over the budget, is refused with a
 * readable message. Ordinary anchors (a shared OpenAPI schema, say) stay well inside it.
 */
import yaml from 'js-yaml';

export const MAX_EXPANDED_NODES = 1_000_000;
export const MAX_EXPANDED_CHARS = 32 * 1024 * 1024;

const TOO_BIG =
  'This YAML file expands (through &anchors and *aliases) far beyond its size, so it was not imported.';

export function measureExpanded(root) {
  const memo = new Map();
  const onStack = new Set();
  const measure = (node) => {
    if (typeof node === 'string') return { nodes: 1, chars: node.length };
    if (node === null || typeof node !== 'object') return { nodes: 1, chars: 0 };
    if (memo.has(node)) return memo.get(node);
    if (onStack.has(node)) throw new Error('This YAML file refers to itself (a cyclic *alias), so it was not imported.');
    onStack.add(node);
    let nodes = 1;
    let chars = 0;
    const children = Array.isArray(node) ? node : Object.keys(node).map((k) => { chars += k.length; return node[k]; });
    for (const child of children) {
      const m = measure(child);
      nodes += m.nodes;
      chars += m.chars;
      if (nodes > MAX_EXPANDED_NODES || chars > MAX_EXPANDED_CHARS) throw new Error(TOO_BIG);
    }
    onStack.delete(node);
    const out = { nodes, chars };
    memo.set(node, out);
    return out;
  };
  return measure(root);
}

/** `yaml.load` + the expanded-size budget. Throws a user-readable Error. */
export function loadImportYaml(text) {
  const data = yaml.load(text);
  measureExpanded(data);
  return data;
}
